import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, NgForm, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { I18nHelperService, TranslateService } from '@xref/ngx';
import { CompetenciesService } from 'src/app/services/competencies/competencies.service';
import { GetStartedService } from 'src/app/services/get-started/get-started.service';
import { IndustriesService } from 'src/app/services/industries/industries.service';
import { QuestionnaireService } from 'src/app/services/questionnaire/questionnaire.service';

import FormSubmitErrorStateMatcher from './error-state-matcher';

export class ChosenCompetencies {
  valid: boolean;
  items: number[];
}

@Component({
    selector: 'xqnr-get-started-form',
    templateUrl: './get-started-form.component.pug',
    styleUrls: ['./get-started-form.component.scss'],
    standalone: false
})
export class GetStartedFormComponent implements OnInit {
  @Input() show: boolean;
  @Output() formSubmitSuccess = new EventEmitter();
  @Output() formCancel = new EventEmitter();

  @ViewChild('formContainer') formContainer: ElementRef;
  @ViewChild('formRef') formRef: NgForm;
  @ViewChild('formSummary') formSummary: ElementRef;

  loaded = false;

  fa = {
    circleNotch: faCircleNotch
  };

  containerHeight: any;
  containerWidth: number;

  competencies = [];
  showingMoreCompetencies = false;
  chosenCompetencies = {};

  industriesList = [];

  guideForm = this.fb.group({
    role: ['', Validators.required],
    industry: ['', Validators.required]
  });

  guideFormSubmitting = false;
  showFormFixMessage = false;
  showCompetencyFixMessage = false;

  formSubmitErrorStateMatcher = new FormSubmitErrorStateMatcher();

  constructor(
    private fb: FormBuilder,
    private cc: CompetenciesService,
    private router: Router,
    private industries: IndustriesService,
    private questionnaire: QuestionnaireService,
    public getStarted: GetStartedService,
    private translate: TranslateService,
    public i18nHelper: I18nHelperService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.getCoreCompetencies();
    this.getIndustries();
  }

  setLoaded() {
    if (this.competencies.length > 0 && this.industriesList.length > 0) {
      this.loaded = true;
      this.cdr.detectChanges();
    } else {
      this.loaded = false;
    }
  }

  getCompetenciesSuccess = (data: any) => {
    this.competencies = data.slice(0, 20);
    this.setDetfaultChosenCompetencies();
    this.showFewerCompetencies();
    this.setLoaded();
  };

  getCoreCompetencies() {
    this.cc.getCompetencies().subscribe(this.getCompetenciesSuccess);
  }

  showMoreCompetencies() {
    this.showingMoreCompetencies = true;
    this.competencies.forEach((item) => item.show = true);
  }

  getChosenCompetenciesList(): ChosenCompetencies {
    const chosenCompetencies = [];

    Object.keys(this.chosenCompetencies).forEach((key) => {
      if (this.chosenCompetencies[key].selected)
        chosenCompetencies.push(key);
    });

    return {
      valid: this.validateChosenCompetencies(chosenCompetencies),
      items: chosenCompetencies
    };
  }

  showFewerCompetencies() {
    this.showingMoreCompetencies = false;
    this.competencies.forEach((item, index) => {
      if (index <= 10)
        item.show = true;
      else
        item.show = false;
    });
  }

  setDetfaultChosenCompetencies() {
    this.competencies.forEach((item) => {
      this.chosenCompetencies[item.key] = {
        selected: false
      };
    });
  }

  validateChosenCompetencies(competencyList: number[]): boolean {
    return competencyList.length >= 3 && competencyList.length <= 7;
  }

  competencySelection() {
    const chosenCompetencies = this.getChosenCompetenciesList();

    if (this.validateChosenCompetencies(chosenCompetencies.items))
      this.showCompetencyFixMessage = false;
    else if (this.formRef.submitted)
      this.showCompetencyFixMessage = true;
  }

  getIndustriesSuccess = (data: any) => {
    this.industriesList = data;
    this.setLoaded();
  };

  getIndustriesError = (err: any) => {
    console.error(err);
  };

  getIndustries() {
    this.industries.getIndustries().subscribe(this.getIndustriesSuccess, this.getIndustriesError);
  }

  defaultQuestionnaireTitleKey() {
    return this.questionnaire.makeDefaultQuestionnaireTitle(
      this.industries.getIndustryTitleById(
        this.industriesList,
        this.guideForm.controls.industry.value as any
      )
    );
  }

  createQuestionnaireSuccess = (result: any) => {
    this.guideFormSubmitting = false;
    this.resetForm();
    this.getStarted.hide();
    this.formSubmitSuccess.emit();
    this.router.navigate(['build', result.questionnaire_id]);
  };

  emitHide() {
    this.formCancel.emit();
  }

  resetForm() {
    this.formRef.resetForm();
    this.setDetfaultChosenCompetencies();
  }

  submit() {
    const chosenCompetencies = this.getChosenCompetenciesList();

    this.showCompetencyFixMessage = !chosenCompetencies.valid;

    if (this.guideForm.valid && chosenCompetencies.valid && !this.guideFormSubmitting) {
      this.showFormFixMessage = false;
      this.guideFormSubmitting = true;

      const data = {
        role: this.guideForm.controls.role.value,
        industry: this.guideForm.controls.industry.value,
        core_competencies: chosenCompetencies.items.map((item: any) => item, 0),
        title: ''
      };

      const titleKey = this.industries.getIndustryTitleById(
        this.industriesList,
        this.guideForm.controls.industry.value as any
      );

      this.translate.get(`industries.${titleKey}`).subscribe((industry: string) => {
        data.title = this.questionnaire.makeDefaultQuestionnaireTitle(industry);
        this.questionnaire.createQuestionnaire(data).subscribe(this.createQuestionnaireSuccess);
      });

    } else {
      this.showFormFixMessage = true;
      this.guideFormSubmitting = false;
    }
  }
}
